import type { PublishedView } from "../../domain/entities.ts";
import type { GroupBy, SavedView, ViewFilters } from "../../domain/app-types.ts";
import type { SortDirection, TaskSort } from "../../domain/entities.ts";
import { normalizePageSize, normalizeSortDirection, normalizeTaskSort } from "../tasks/selectors.ts";
import { openSavedViewDialog } from "./saved-view-dialog-controller.ts";
import type { SavedViewDialogModel } from "./saved-view-dialog-view.ts";
import { canManageSavedView, canMoveSavedView } from "./saved-view-state.ts";
import { isCrossSpace } from "./view-scope.ts";

export interface SavedViewWorkflowState {
  savedViews: SavedView[];
  activeSavedView: string | null;
  activeRoot: string | null;
  groupBy: GroupBy;
  viewSort: TaskSort;
  viewSortDirection: SortDirection;
  viewPageSize: number;
}

export interface SavedViewWorkflowCapabilities {
  beforeApply?(filters?: ViewFilters): Promise<void>;
  state(): SavedViewWorkflowState;
  setSavedViews(views: SavedView[]): void;
  setViewState(update: { filters?: ViewFilters; groupBy?: GroupBy; sortBy?: TaskSort; sortDirection?: SortDirection; pageSize?: number; activeSavedView?: string | null; filter?: "open" | "custom"; taskPage?: number; categoryFilter?: null }): void;
  defaultFilters(): ViewFilters;
  viewsForRoot(rootId: string | null): SavedView[];
  buildModel(availableTags: string[]): Omit<SavedViewDialogModel, "availableTags">;
  loadAvailableTags(): Promise<string[]>;
  persistPersonalViews(): Promise<void>;
  updateView(id: string, update: (view: SavedView) => void): Promise<void>;
  deletePublished(view: SavedView): Promise<void>;
  createPublished(view: PublishedView): Promise<void>;
  randomId(): string;
  markLocal(): void;
  sync(): void;
  render(): Promise<void>;
  toast(message: string, error?: boolean): void;
}

export function createSavedViewWorkflow(capabilities: SavedViewWorkflowCapabilities) {
  return { open: () => openSavedViewWorkflow(capabilities) };
}

export async function openSavedViewWorkflow(capabilities: SavedViewWorkflowCapabilities): Promise<void> {
  const initial = capabilities.state();
  const activeView = initial.savedViews.find(view => view.id === initial.activeSavedView);
  const availableTags = await capabilities.loadAvailableTags();
  const owner = () => capabilities.buildModel(availableTags).activeRootOwner;
  const canChange = (view: SavedView): boolean => canManageSavedView(view, owner() && view.rootId === capabilities.state().activeRoot);
  const requireChangeAccess = (view: SavedView): boolean => {
    if (canChange(view)) return true;
    capabilities.toast("Only space owners can change shared views. Duplicate this view for your own editable copy.", true);
    return false;
  };
  openSavedViewDialog({
    model: () => ({ ...capabilities.buildModel(availableTags), availableTags }),
    applyView: async id => {
      const view = capabilities.viewsForRoot(capabilities.state().activeRoot).find(item => item.id === id);
      if (!view) { capabilities.toast("That view is no longer available", true); return; }
      await capabilities.beforeApply?.(view.filters);
      capabilities.setViewState({ filters: structuredClone(view.filters), groupBy: view.groupBy, sortBy: normalizeTaskSort(view.sortBy), sortDirection: normalizeSortDirection(view.sortDirection), pageSize: normalizePageSize(view.pageSize), taskPage: 1, categoryFilter: null, filter: "custom", activeSavedView: view.id });
      await capabilities.render();
    },
    clear: async () => {
      capabilities.setViewState({ filters: capabilities.defaultFilters(), groupBy: "none", sortBy: "manual", sortDirection: "asc", pageSize: 0, taskPage: 1, filter: "open", activeSavedView: null });
      await capabilities.render();
    },
    deleteView: async id => {
      const state = capabilities.state();
      const target = state.savedViews.find(view => view.id === id);
      if (!target || !requireChangeAccess(target)) return;
      if (target?.published && target.rootId) { await capabilities.deletePublished(target); capabilities.markLocal(); capabilities.sync(); }
      capabilities.setSavedViews(state.savedViews.filter(view => view.id !== id));
      await capabilities.persistPersonalViews();
      if (state.activeSavedView === id) capabilities.setViewState({ activeSavedView: null, filter: "open", groupBy: "none", sortBy: "manual", sortDirection: "asc", pageSize: 0, taskPage: 1 });
      await capabilities.render();
      capabilities.toast("Saved view deleted");
    },
    duplicateView: async id => {
      const state = capabilities.state();
      const source = state.savedViews.find(view => view.id === id);
      if (!source) return;
      capabilities.setSavedViews([...state.savedViews, { ...structuredClone(source), id: capabilities.randomId(), name: `${source.name} copy`, isDefault: false, position: capabilities.viewsForRoot(state.activeRoot).length, published: false }]);
      await capabilities.persistPersonalViews();
    },
    publishView: async id => {
      const state = capabilities.state();
      const source = state.savedViews.find(view => view.id === id);
      if (!source || !state.activeRoot || !capabilities.buildModel(availableTags).activeRootOwner) return;
      if (isCrossSpace(source.filters)) { capabilities.toast("Cross-space views stay private on this device.", true); return; }
      const now = Date.now();
      const published: PublishedView = { id: capabilities.randomId(), rootId: state.activeRoot, name: source.name, filters: structuredClone(source.filters), groupBy: source.groupBy, sortBy: normalizeTaskSort(source.sortBy), sortDirection: source.sortDirection, pageSize: source.pageSize, position: capabilities.viewsForRoot(state.activeRoot).length, isDefault: false, deleted: false, createdAt: now, updatedAt: now };
      await capabilities.createPublished(published);
      capabilities.setSavedViews([...state.savedViews, { ...published, published: true }]);
      capabilities.markLocal();
      capabilities.toast("View published to this space");
      capabilities.sync();
    },
    toggleDefault: async id => {
      const state = capabilities.state();
      const target = state.savedViews.find(view => view.id === id);
      if (!target || !requireChangeAccess(target)) return;
      const next = !target.isDefault;
      for (const view of state.savedViews.filter(view => view.rootId === target.rootId && !!view.published === !!target.published)) await capabilities.updateView(view.id, draft => { draft.isDefault = view.id === target.id ? next : false; });
    },
    moveView: async (id, direction) => {
      const views = capabilities.viewsForRoot(capabilities.state().activeRoot);
      const index = views.findIndex(view => view.id === id);
      const next = index + (direction === "up" ? -1 : 1);
      if (!canMoveSavedView(views, index, direction, owner())) return;
      [views[index], views[next]] = [views[next], views[index]];
      for (let position = 0; position < views.length; position++) if (canChange(views[position])) await capabilities.updateView(views[position].id, view => { view.position = position; });
    },
    submit: async submission => {
      if (submission.mode === "save" && activeView?.published) {
        if (isCrossSpace(submission.filters)) { capabilities.toast("Duplicate this shared view first to keep a personal cross-space view.", true); return false; }
        const current = capabilities.state().savedViews.find(view => view.id === activeView.id);
        if (!current || !requireChangeAccess(current)) return false;
      }
      const sortBy = normalizeTaskSort(submission.sortBy);
      const sortDirection = normalizeSortDirection(submission.sortDirection);
      const pageSize = normalizePageSize(submission.pageSize);
      await capabilities.beforeApply?.(submission.filters);
      capabilities.setViewState({ filters: submission.filters, groupBy: submission.groupBy, sortBy, sortDirection, pageSize, taskPage: 1, categoryFilter: null, filter: "custom" });
      if (submission.mode === "save") {
        if (!submission.name) { capabilities.toast("Add a name before saving this view", true); return false; }
        const state = capabilities.state();
        const view: SavedView = { id: activeView?.id || capabilities.randomId(), name: submission.name, rootId: state.activeRoot || undefined, filters: structuredClone(submission.filters), groupBy: submission.groupBy, sortBy, sortDirection, pageSize, position: activeView?.position ?? capabilities.viewsForRoot(state.activeRoot).length, isDefault: activeView?.isDefault || false, published: activeView?.published };
        if (activeView?.published) await capabilities.updateView(activeView.id, target => Object.assign(target, view));
        else { capabilities.setSavedViews(activeView ? state.savedViews.map(item => item.id === view.id ? view : item) : [...state.savedViews, view]); await capabilities.persistPersonalViews(); }
        capabilities.setViewState({ activeSavedView: view.id });
        capabilities.toast(activeView ? "Saved view updated" : "View saved");
      } else capabilities.setViewState({ activeSavedView: null });
      await capabilities.render();
      return true;
    },
  });
}
